import { EventEmitter, Injectable } from '@angular/core';
import { Headers, Http, RequestOptions } from '@angular/http';
import { IToken } from '../types/api-response/token';
import { environment } from '../../environments/environment';
import { catchError, map, take, tap } from 'rxjs/operators';
import { Observable } from 'rxjs/Observable';
import { of } from 'rxjs/observable/of';
import { from } from 'rxjs/observable/from';
import { Router } from '@angular/router';
import { CookieService } from './cookie.service';
import { Subject } from 'rxjs/Subject';

@Injectable()
export class AuthTokenService {
    /**
     * Token promise
     */
    private refreshingTokenPromise: Promise<string>;

    /**
     *  Access token revoke flag
     */
    private isForceRefreshToken = false;

    /**
     * On token error event
     */
    public tokenError$: EventEmitter<string> = new EventEmitter();

    /**
     * On token update event
     */
    public tokenUpdate$: Subject<any> = new Subject<any>();

    constructor(
      private http: Http,
      public router: Router,
      public cookieService: CookieService
    ) {}

    /**
     * Returns saved token string from local storage
     * TODO: Remove this arg. Use getAccessToken() property.
     */
    public get accessToken(): string {
        const tokenDataStr: string = localStorage.getItem('mcc_token');
        let token: IToken;

        try {
            token = JSON.parse(tokenDataStr);

            return token.access_token;
        } catch (e) {
            return localStorage.getItem('token'); // backward compatibility
        }
    }

    /**
     * Sets token details to local storage
     *
     * @param token
     */
    public setAccessToken(token: IToken): void {
        localStorage.setItem('mcc_token', JSON.stringify(token));
        this.tokenUpdate$.next();

        // uses with other apps on subdomains
        this.cookieService.set( 'token', token.access_token, new Date(token.expires_at), '/', '.' + environment.domainName);
    }

    /**
     * Removes stored token data
     */
    public removeAccessToken() {
        localStorage.removeItem('mcc_token');
        localStorage.removeItem('token'); // todo: remove this line after some time
        this.refreshingTokenPromise = null;
        this.cookieService.delete('token', '/', '.' + environment.domainName);
    }

    /**
     * Returns access token
     */
    public getAccessToken(forceRefresh = this.isForceRefreshToken): Observable<string | null> {
        const tokenData: string = localStorage.getItem('mcc_token');
        const token: IToken = tokenData ? JSON.parse(tokenData) : null;

        if (!token) {
            const oldTokenString = localStorage.getItem('token');
            if (oldTokenString) {
                return of(oldTokenString); // backward compatibility: return string
            }

            console.error('Token data does not exist');
            this.tokenError$.emit('Token data does not exist');

            return of(null);
        }

        if (token.expires_at > Date.now() && !forceRefresh && !this.refreshingTokenPromise) {
            return of(token.access_token); // if not expired, return current token
        }

        if (!this.refreshingTokenPromise) {
            if (!token.refresh_token) {
                return of(null);
            }
            this.isForceRefreshToken = false; // reset token revoke flag
            this.refreshingTokenPromise = this.refreshToken(token.refresh_token).pipe(
                map(newToken => {
                    this.refreshingTokenPromise = null; // force clean refreshing promise

                    return newToken.access_token;
                }),
                take(1)
            ).toPromise();
        }

        return from(this.refreshingTokenPromise);
    }

    /**
     * Getting access token using refresh token
     */
    private refreshToken(refreshToken: string) {
        if(!refreshToken) {
            throw Observable.throw('Failed to authenticate. Empty refresh token.');
        }

        return this.authenticateWithRefreshToken(refreshToken).pipe(
          tap(token => this.setAccessToken(token)),
          catchError(error => {
              // this.tokenError$.emit(error);
              this.tokenError$.emit();

              return Observable.throw(error);
          })
        );
    }

    /**
     * Don't use current access token
     * and initialize token refresh process
     * on next request
     */
    revokeCurrentAccessToken() {
        if(!this.refreshingTokenPromise) {
            this.isForceRefreshToken = true;
        }
    }

    /**
     * Returns user authentication token details
     *
     * @param params
     */
    private authenticate(params: {[key: string]: string} = {}): Observable<IToken> {
        params = {
            ...params,
            client_id: environment.oauthClientId,
            client_secret: environment.oauthClientSecret
        };

        const paramsArray: string[] = Object.keys(params).map(keyName => `${keyName}=${params[keyName]}`);
        const paramsStr =  paramsArray.length ? '?' + paramsArray.join('&') : '';
        const url = `${environment.oauthUrl}/oauth/v2/token` + paramsStr;
        const options = new RequestOptions({
            headers: new Headers({ 'Content-Type': 'application/json' })
        });

        return this.http.get(url, options).pipe(
          map((response) => response.json()),
          map((response) => {
              const expires_at = Date.now() + (response.expires_in - 600) * 1000; // 600 seconds - reserve 10 minutes
              delete response.expires_in;

              return {...response, expires_at};
          }),
        );
    }

    /**
     * User authentication with username and password
     *
     * @param username
     * @param password
     */
    public authenticateWithPassword(username: string, password: string): Observable<IToken> {
        return this.authenticate({ username, password, grant_type: 'password' });
    }

    /**
     * User authentication with refresh token
     *
     * @param refresh_token
     */
    public authenticateWithRefreshToken(refresh_token: string): Observable<IToken> {
        return this.authenticate({ refresh_token, grant_type: 'refresh_token' });
    }
}



// WEBPACK FOOTER //
// /app/src/app/services/auth-token.service.ts